// Semantic wrapper over the shadcn `toast()` so callers express INTENT
// (success / error / warning / info) and the variant + tone stay consistent
// across the app. No new state system — just a terse, importable facade over the
// toast store's module-level `toast()`. See docs/notifications.md.

import { toast } from "../components/ui/use-toast";

// Toast variants defined by the Toast component (see components/ui/toast.tsx).
type ToastVariant = "default" | "success" | "destructive" | "warning" | "info";

export interface NotifyOptions {
  /** Optional secondary line under the title. */
  description?: string;
}

type ToastReturn = ReturnType<typeof toast>;

// Build + fire a toast with a FIXED variant for the given intent, so styling is
// never a per-call decision.
function fire(
  variant: ToastVariant,
  title: string,
  options?: NotifyOptions
): ToastReturn {
  return toast({ variant, title, description: options?.description });
}

// The public surface: callable as `notify("msg")` for a neutral toast, plus
// intent-tagged helpers. Declared as an interface so the attached methods are
// type-checked (a plain `function notify` + property assignment would not be).
interface Notify {
  (title: string, options?: NotifyOptions): ToastReturn;
  success(title: string, options?: NotifyOptions): ToastReturn;
  error(title: string, options?: NotifyOptions): ToastReturn;
  warning(title: string, options?: NotifyOptions): ToastReturn;
  info(title: string, options?: NotifyOptions): ToastReturn;
  /**
   * Run a promise and toast success or error when it settles. `error` can be a
   * string or a function of the rejection (e.g. pass `getUserMessage`). Re-throws
   * so callers keep control of their own flow.
   */
  promise<T>(
    promise: Promise<T>,
    messages: { success: string; error: string | ((err: unknown) => string) }
  ): Promise<T>;
}

const notifyBase = (title: string, options?: NotifyOptions): ToastReturn =>
  fire("default", title, options);

export const notify: Notify = Object.assign(notifyBase, {
  success: (title: string, options?: NotifyOptions) =>
    fire("success", title, options),
  // "error" intent maps to the destructive variant.
  error: (title: string, options?: NotifyOptions) =>
    fire("destructive", title, options),
  warning: (title: string, options?: NotifyOptions) =>
    fire("warning", title, options),
  info: (title: string, options?: NotifyOptions) =>
    fire("info", title, options),
  promise: async <T>(
    promise: Promise<T>,
    messages: { success: string; error: string | ((err: unknown) => string) }
  ): Promise<T> => {
    try {
      const result = await promise;
      fire("success", messages.success);
      return result;
    } catch (err) {
      const msg =
        typeof messages.error === "function"
          ? messages.error(err)
          : messages.error;
      fire("destructive", msg);
      throw err; // re-throw so the caller's catch/return logic still runs
    }
  },
});
